'use client';

/**
 * useCheckoutAddress Hook
 *
 * Focused hook for address operations.
 * Handles shipping and billing address updates.
 */

import { useCallback } from 'react';
import { useSnapshot } from 'valtio';

import { localizeApiErrors } from '@/lib/checkout/errors/localizeApiErrors';
import { resolveExpressCheckoutEmailForDisplay } from '@/lib/checkout/express/expressCheckoutAddress';
import {
  checkoutBillingAddressUpdate,
  checkoutEmailUpdate,
  checkoutShippingAddressUpdate,
} from '@/lib/checkout/operations';
import type { ApiError } from '@/lib/checkout/types';
import { checkoutActions, checkoutState } from '@/states/checkout';

function localizeErrors(errors: ApiError[], locale: string): ApiError[] {
  return localizeApiErrors(errors, locale);
}

/**
 * Map API errors to simple format
 */
function mapErrors(errors: ApiError[]): Array<{ code: string; message: string; field?: string }> {
  return errors.map((e) => ({
    code: e.code || 'UNKNOWN',
    message: e.message || 'An error occurred',
    field: e.field || undefined,
  }));
}

/**
 * Address input type
 */
export interface AddressInput {
  firstName: string;
  lastName: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  cityArea?: string;
  postalCode: string;
  country: string;
  countryArea?: string;
  phone?: string;
  companyName?: string;
  skipValidation?: boolean;
  metadata?: { key: string; value: string }[];
}

/**
 * Address operation result
 */
export interface AddressOperationResult {
  success: boolean;
  errors: Array<{ code: string; message: string; field?: string }>;
}

/**
 * Hook options
 */
export interface UseCheckoutAddressOptions {
  locale?: string;
}

/**
 * Focused hook for address operations
 */
export function useCheckoutAddress(options: UseCheckoutAddressOptions = {}) {
  const snapshot = useSnapshot(checkoutState);

  // Parse locale
  const locale = options.locale || 'us-en';
  const [, languageSlug = 'en'] = locale.split('-');

  /**
   * Update customer email
   */
  const updateEmail = useCallback(
    async (email: string): Promise<AddressOperationResult> => {
      if (!checkoutState.id) {
        const localized = localizeErrors(
          [{ code: 'NO_CHECKOUT', message: 'No checkout found' }],
          locale
        );
        return {
          success: false,
          errors: mapErrors(localized),
        };
      }

      // Skip the Saleor email update when the checkout already has this email.
      // Logged-in users have their email attached to the checkout at creation,
      // and Saleor's `checkoutEmailUpdate` rejects no-op writes against an
      // already-set email tied to the user account, surfacing as "Failed to
      // update email" on the delivery step.
      const currentEmail = (
        checkoutState.checkout?.email ??
        checkoutState.email ??
        ''
      ).toLowerCase();
      if (currentEmail && currentEmail === email.toLowerCase()) {
        checkoutActions.setEmail(email);
        return { success: true, errors: [] };
      }

      checkoutActions.setProcessing(true);
      checkoutActions.clearErrors();

      try {
        // Pass languageCode like shipping/billing — without it Saleor defaults to
        // EN_US and attribute translations drop (cart flashes "STSTL - V-DIAL-…").
        const { checkout, errors } = await checkoutEmailUpdate({
          id: checkoutState.id,
          email,
          languageCode: languageSlug.toUpperCase(),
        });

        if (errors?.length) {
          const localizedErrors = localizeErrors(errors, locale);
          checkoutActions.setErrors(localizedErrors, 'checkout');
          return { success: false, errors: mapErrors(localizedErrors) };
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
        }
        checkoutActions.setEmail(email);

        return { success: true, errors: [] };
      } finally {
        checkoutActions.setProcessing(false);
      }
    },
    [languageSlug, locale]
  );

  /**
   * Update shipping address
   */
  const updateShippingAddress = useCallback(
    async (address: AddressInput): Promise<AddressOperationResult> => {
      if (!checkoutState.id) {
        const localized = localizeErrors(
          [{ code: 'NO_CHECKOUT', message: 'No checkout found' }],
          locale
        );
        return {
          success: false,
          errors: mapErrors(localized),
        };
      }

      checkoutActions.setProcessing(true);
      checkoutActions.clearErrors();

      try {
        const { checkout, errors } = await checkoutShippingAddressUpdate({
          id: checkoutState.id,
          address,
          languageCode: languageSlug.toUpperCase(),
        });

        if (errors?.length) {
          const localizedErrors = localizeErrors(errors, locale);
          checkoutActions.setErrors(localizedErrors, 'checkout');
          return { success: false, errors: mapErrors(localizedErrors) };
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
        }
        checkoutActions.setShippingAddress(address);

        return { success: true, errors: [] };
      } finally {
        checkoutActions.setProcessing(false);
      }
    },
    [languageSlug, locale]
  );

  /**
   * Update billing address
   */
  const updateBillingAddress = useCallback(
    async (address: AddressInput): Promise<AddressOperationResult> => {
      if (!checkoutState.id) {
        const localized = localizeErrors(
          [{ code: 'NO_CHECKOUT', message: 'No checkout found' }],
          locale
        );
        return {
          success: false,
          errors: mapErrors(localized),
        };
      }

      checkoutActions.setProcessing(true);
      checkoutActions.clearErrors();

      try {
        const { checkout, errors } = await checkoutBillingAddressUpdate({
          id: checkoutState.id,
          address,
          languageCode: languageSlug.toUpperCase(),
        });

        if (errors?.length) {
          const localizedErrors = localizeErrors(errors, locale);
          checkoutActions.setErrors(localizedErrors, 'checkout');
          return { success: false, errors: mapErrors(localizedErrors) };
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
        }
        checkoutActions.setBillingAddress(address);

        return { success: true, errors: [] };
      } finally {
        checkoutActions.setProcessing(false);
      }
    },
    [languageSlug, locale]
  );

  /**
   * Set billing address same as shipping
   */
  const setBillingSameAsShipping = useCallback(async (): Promise<AddressOperationResult> => {
    const shippingAddress = checkoutState.shippingAddress;

    if (!shippingAddress) {
      const localized = localizeErrors(
        [{ code: 'NO_SHIPPING_ADDRESS', message: 'No shipping address set' }],
        locale
      );
      return {
        success: false,
        errors: mapErrors(localized),
      };
    }

    return updateBillingAddress(shippingAddress);
  }, [updateBillingAddress, locale]);

  /**
   * Clear addresses
   */
  const clearAddresses = useCallback(() => {
    // Clear addresses by setting empty values
    // The state actions will handle the null/undefined conversion
    checkoutActions.setShippingAddress(
      undefined as unknown as Parameters<typeof checkoutActions.setShippingAddress>[0]
    );
    checkoutActions.setBillingAddress(
      undefined as unknown as Parameters<typeof checkoutActions.setBillingAddress>[0]
    );
  }, []);

  return {
    // State
    email: resolveExpressCheckoutEmailForDisplay(snapshot.email),
    shippingAddress: snapshot.shippingAddress,
    billingAddress: snapshot.billingAddress,
    hasShippingAddress: !!snapshot.shippingAddress,
    hasBillingAddress: !!snapshot.billingAddress,
    isProcessing: snapshot.isProcessing,
    errors: snapshot.errors,

    // Operations
    updateEmail,
    updateShippingAddress,
    updateBillingAddress,
    setBillingSameAsShipping,
    clearAddresses,
  };
}
